import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, Circle, Loader2 } from "lucide-react";

export function SopStrip({ date, currency = "INR", nonce = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sop/progress?date=${date}&currency=${currency}`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date, currency]);

  useEffect(() => {
    load();
  }, [load, nonce]);

  if (loading) {
    return (
      <Card className="p-6 flex justify-center" data-testid="sop-strip-loading">
        <Loader2 className="h-5 w-5 animate-spin text-brand" />
      </Card>
    );
  }
  if (!data) return null;

  return (
    <Card className="p-5 space-y-4" data-testid="sop-strip">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-heading text-sm tracking-tight">Daily SOP · {data.date}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {data.complete
              ? "Every step done — the day is fully booked and signed off."
              : "Work top to bottom. Each step feeds the next."}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate("/sop")}
          className="text-muted-foreground hover:text-foreground gap-1.5"
          data-testid="sop-strip-full-link"
        >
          Full SOP
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid gap-2 lg:grid-cols-5">
        {data.steps.map((s) => {
          const isNext = data.next_step === s.id;
          return (
            <button
              key={s.id}
              onClick={() => navigate(s.route)}
              data-testid={`sop-step-${s.id}`}
              className={`text-left rounded-lg border p-3 transition-colors ${
                s.done
                  ? "border-emerald-400/30 bg-emerald-400/[0.06]"
                  : isNext
                  ? "border-brand/40 bg-brand/[0.07]"
                  : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04]"
              }`}
            >
              <div className="flex items-center gap-2">
                {s.done ? (
                  <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                ) : (
                  <Circle
                    className={`h-3.5 w-3.5 shrink-0 ${isNext ? "text-brand" : "text-muted-foreground"}`}
                  />
                )}
                <p className="text-xs font-medium truncate">{s.title}</p>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">{s.detail}</p>
              {isNext && (
                <p className="text-[10px] uppercase tracking-wider text-brand mt-1.5">Do this next</p>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}
